import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../api/client.js';
import { useApiQuery } from '../../api/useApiQuery.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.jsx';

export function PublishHistoryPage() {
  const { menuId } = useParams();
  const { data: history, loading, error, reload } = useApiQuery(() => api.get(`/api/menus/${menuId}/publish/history`), [menuId]);
  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [rollbackError, setRollbackError] = useState(null);
  const [message, setMessage] = useState(null);

  async function handleRollback() {
    setRestoring(true);
    setRollbackError(null);
    try {
      await api.post(`/api/menus/${menuId}/publish/rollback/${rollbackTarget.version}`);
      setMessage({ type: 'success', text: `Restored version ${rollbackTarget.version} as the new live version.` });
      setRollbackTarget(null);
      reload();
    } catch (err) {
      setRollbackError(err.message);
    } finally {
      setRestoring(false);
    }
  }

  async function handleUnpublish() {
    setUnpublishing(true);
    try {
      await api.post(`/api/menus/${menuId}/publish/unpublish`);
      setMessage({ type: 'success', text: 'Menu taken offline. Customers will no longer see it.' });
      reload();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setUnpublishing(false);
      setConfirmUnpublish(false);
    }
  }

  const historyItems = history ?? [];
  const current = historyItems.find((h) => h.isCurrent);

  return (
    <div className="page publish-history-page">
      <div className="page-header">
        <div>
          <h1>Publish history</h1>
          <Link className="back-link" to={`/menus/${menuId}`}>← Back to menu</Link>
        </div>
        {current && (
          <button type="button" className="btn btn-danger" onClick={() => setConfirmUnpublish(true)} disabled={unpublishing}>
            Take menu offline
          </button>
        )}
      </div>

      <div className="history-intro">
        <p>Every publish creates a snapshot. Customers always see the version marked <strong>Live</strong>, and restoring an older version creates a new publish so nothing is lost.</p>
      </div>

      {message && <div className={message.type === 'success' ? 'alert alert-success' : 'alert alert-error'}>{message.text}</div>}

      {loading ? (
        <span className="spinner" aria-label="Loading" />
      ) : error ? (
        <div className="alert alert-error">{error.message}</div>
      ) : historyItems.length === 0 ? (
        <div className="empty">
          <strong>Never published</strong>
          <p>Publish this menu to see its version history here.</p>
          <Link to={`/menus/${menuId}`} className="btn btn-primary">Back to menu editor</Link>
        </div>
      ) : (
        <div className="history-list">
          {historyItems.map((h) => (
            <div key={h.id} className={`history-row${h.isCurrent ? ' history-row--current' : ''}`}>
              <div className="history-row__content">
                <div className="history-row__title">
                  <strong>Version {h.version}</strong>
                  {h.isCurrent && <span className="badge badge-success">Live now</span>}
                </div>
                <div className="field-hint">Published {new Date(h.publishedAt).toLocaleString()}</div>
              </div>
              {!h.isCurrent && (
                <button type="button" className="btn btn-sm" onClick={() => setRollbackTarget(h)}>
                  Restore this version
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {confirmUnpublish && (
        <ConfirmDialog
          title="Take this menu offline?"
          description="Customers will no longer be able to open this menu. Your publish history stays safe, and you can publish it again from the menu editor at any time."
          confirmLabel="Take offline"
          busy={unpublishing}
          onConfirm={handleUnpublish}
          onCancel={() => setConfirmUnpublish(false)}
        />
      )}

      {rollbackTarget && (
        <ConfirmDialog
          title={`Restore version ${rollbackTarget.version}?`}
          description="This publishes that version's content as the new live version. Nothing is lost — it becomes a new entry in this history."
          confirmLabel="Restore"
          danger={false}
          busy={restoring}
          error={rollbackError}
          onConfirm={handleRollback}
          onCancel={() => setRollbackTarget(null)}
        />
      )}
    </div>
  );
}
